import React, { useEffect, useMemo, useState } from "react";
import { Organisation } from "@/types/Organisation";
import organisationApi from "@/api/organisationApi";
import adminApi from "@/api/adminApi";
import ClipLoader from "react-spinners/ClipLoader";
import { toast } from "react-toastify";

interface OrganisationSwitchModalProps {
    isOpen: boolean;
    onClose: () => void;
}

const OrganisationSwitchModal: React.FC<OrganisationSwitchModalProps> = ({ isOpen, onClose }) => {
    const [organisations, setOrganisations] = useState<Organisation[]>([]);
    const [loading, setLoading] = useState(false);
    const [submitting, setSubmitting] = useState(false);
    const [search, setSearch] = useState("");
    const [selectedOrgId, setSelectedOrgId] = useState<number | null>(null);

    useEffect(() => {
        if (!isOpen) {
            setSearch("");
            setSelectedOrgId(null);
            return;
        }

        const fetchOrganisations = async () => {
            setLoading(true);
            try {
                const response = await organisationApi.getAll();
                setOrganisations(response.data || []);
            } catch (error) {
                console.error("Failed to load organisations", error);
                toast.error("Kunde inte ladda organisationer. Försök igen.");
            } finally {
                setLoading(false);
            }
        };

        fetchOrganisations();
    }, [isOpen]);

    const filteredOrganisations = useMemo(() => {
        const term = search.trim().toLowerCase();
        if (!term) return organisations;
        return organisations.filter((org) =>
            org.name.toLowerCase().includes(term) ||
            org.identifierValue.toLowerCase().includes(term)
        );
    }, [organisations, search]);

    const handleConfirm = async () => {
        if (!selectedOrgId) {
            toast.warning("Välj en organisation först.");
            return;
        }

        setSubmitting(true);
        try {
            await adminApi.switchCurrentUserOrganisation(selectedOrgId);
            // On success, reload full page as requested
            window.location.reload();
        } catch (error) {
            console.error("Failed to switch organisation", error);
            toast.error("Kunde inte byta organisation. Försök igen.");
        } finally {
            setSubmitting(false);
        }
    };

    if (!isOpen) {
        return null;
    }

    return (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-40">
            <div className="bg-white rounded-lg shadow-xl w-full max-w-lg mx-4">
                <div className="px-6 py-4 border-b border-gray-200 flex items-center justify-between">
                    <h2 className="text-lg font-semibold text-gray-900">
                        Byt organisation
                    </h2>
                    <button
                        type="button"
                        onClick={onClose}
                        className="text-gray-400 hover:text-gray-600"
                    >
                        <span className="sr-only">Stäng</span>
                        <svg className="w-5 h-5" viewBox="0 0 20 20" fill="currentColor">
                            <path
                                fillRule="evenodd"
                                d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z"
                                clipRule="evenodd"
                            />
                        </svg>
                    </button>
                </div>

                <div className="px-6 py-4 space-y-4">
                    <p className="text-sm text-gray-700">
                        Denna funktion används för att växla mellan olika organisationer i Coach Hub som administratör.
                    </p>

                    {loading ? (
                        <div className="flex items-center justify-center py-8">
                            <ClipLoader size={32} color="#16a34a" />
                        </div>
                    ) : (
                        <>
                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-1">
                                    Sök organisation
                                </label>
                                <input
                                    type="text"
                                    value={search}
                                    onChange={(e) => setSearch(e.target.value)}
                                    placeholder="Sök på namn eller id..."
                                    className="w-full rounded-md border border-gray-300 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500"
                                />
                            </div>

                            <div className="max-h-64 overflow-y-auto border border-gray-200 rounded-md">
                                {filteredOrganisations.length === 0 ? (
                                    <div className="p-4 text-sm text-gray-500">
                                        Inga organisationer matchar din sökning.
                                    </div>
                                ) : (
                                    <ul className="divide-y divide-gray-100">
                                        {filteredOrganisations.map((org) => (
                                            <li key={org.id} className="flex items-center">
                                                <button
                                                    type="button"
                                                    onClick={() => setSelectedOrgId(org.id ?? 0)}
                                                    className={`w-full px-4 py-2 text-left text-sm flex items-center justify-between ${
                                                        selectedOrgId === org.id
                                                            ? "bg-emerald-50"
                                                            : "hover:bg-gray-50"
                                                    }`}
                                                >
                                                    <div>
                                                        <div className="font-medium text-gray-900">
                                                            {org.name}
                                                        </div>
                                                        <div className="text-xs text-gray-500">
                                                            {org.identifierValue}
                                                        </div>
                                                    </div>
                                                    <div
                                                        className={`w-4 h-4 rounded-full border flex items-center justify-center ${
                                                            selectedOrgId === org.id
                                                                ? "border-emerald-600 bg-emerald-600"
                                                                : "border-gray-300"
                                                        }`}
                                                    >
                                                        {selectedOrgId === org.id && (
                                                            <span className="w-2 h-2 rounded-full bg-white" />
                                                        )}
                                                    </div>
                                                </button>
                                            </li>
                                        ))}
                                    </ul>
                                )}
                            </div>
                        </>
                    )}
                </div>

                <div className="px-6 py-4 border-t border-gray-200 flex justify-end gap-3">
                    <button
                        type="button"
                        onClick={onClose}
                        className="px-4 py-2 text-sm rounded-md border border-gray-300 text-gray-700 hover:bg-gray-50"
                        disabled={submitting}
                    >
                        Avbryt
                    </button>
                    <button
                        type="button"
                        onClick={handleConfirm}
                        disabled={submitting || !selectedOrgId}
                        className="px-4 py-2 text-sm rounded-md bg-emerald-600 text-white hover:bg-emerald-700 disabled:opacity-60 disabled:cursor-not-allowed flex items-center gap-2"
                    >
                        {submitting && (
                            <ClipLoader size={16} color="#ffffff" />
                        )}
                        <span>Byt organisation</span>
                    </button>
                </div>
            </div>
        </div>
    );
};

export default OrganisationSwitchModal;

